import { makeAutoObservable } from "mobx";
import { debounce } from "lodash";
import {
  createHoldingEntity,
  deleteHoldingEntity,
  getHoldingEntityData,
  updateHoldingEntity,
  getConnections,
  createConnection,
  updateConnection,
  deleteConnection,
  reassignConnection,
  reArrangeGraph
} from "./entities.service";
import { MarkerType } from "@xyflow/react";
import { errorNotification, successNotification } from "../../layout/components/Notifications/notifications";
import { getEntityColor, getNewPositionForNewNode } from "../utils/graphUtils";
// Layout constants - matching backend spacing
const nodeWidth = 180;
const nodeHeight = 100;
const nodeSpacing = { x: 30, y: 20 };

export class EntitiesStore {

  // Data states
  isLoaded = false;
  isLoading = false;
  data = [];
  connections = [];

  // Selection states  
  selectedEntityId = null;
  selectedConnectionId = null;

  // Hover states
  hoveredEntityId = null;

  // UI states
  isCanvasLocked = false;

  // Modal states (moved from component)
  showEditEdgeModal = false;
  showAddModal = false;
  showEditModal = false;
  showDeleteModal = false;
  showEntityListModal = false;
  editingEntity = null;
  editingEdge = null;

  // Popover state management
  openPopoverId = null;
  editingPopoverEdge = null;
  onTemporaryEdgeRemove = null; // Callback to remove temp edge from component state

  // Loading states for specific operations
  isCreatingConnection = false;
  isUpdatingConnection = false;
  isDeletingConnection = false;
  isUpdatingNodePosition = false;

  // Legacy compatibility
  relationships = [];

  constructor(metadataStore, itemStore, filtersStore) {
    makeAutoObservable(this);
    this.metadataStore = metadataStore;
    this.itemStore = itemStore;
    this.filtersStore = filtersStore;

    // Create debounced function for position updates
    this.debouncedUpdatePosition = debounce(this._updateNodePositionImmediate.bind(this), 500);
  }

  // ========== LOADING STATE MANAGEMENT ==========
  setLoading = (loading) => {
    this.isLoading = loading;
    this.isLoaded = !loading;
  }

  setConnectionLoading = (operation, loading) => {
    switch (operation) {
      case 'create':
        this.isCreatingConnection = loading;
        break;
      case 'update':
        this.isUpdatingConnection = loading;
        break;
      case 'delete':
        this.isDeletingConnection = loading;
        break;
      default:
        // No operation specified
        break;
    }
  }

  // ========== MODAL STATE MANAGEMENT ==========
  setEditingEdge = (edge) => {
    this.editingEdge = edge;
    if (edge) {
      this.selectedConnectionId = edge.id;
    }
  }

  setShowEditEdgeModal = (show, edge = null) => {
    this.showEditEdgeModal = show;
    if (edge) {
      this.editingEdge = edge;
      this.selectedConnectionId = edge.id;
    }
  }

  setShowAddModal = (show) => {
    this.showAddModal = show;
  }

  setShowEditModal = (show, entity = null) => {
    this.editingEntity = entity;
    this.showEditModal = show;
  }

  setShowDeleteModal = (show) => {
    this.showDeleteModal = show;
  }

  setShowEntityListModal = (show) => {
    this.showEntityListModal = show;
  }

  // ========== UI ACTION HANDLERS ==========
  handleShowEntityList = () => {
    this.setShowEntityListModal(true);
  }

  handleRearrange = async () => {
    if (this.data.length <= 1) return;

    this.setLoading(true);
    try {
      const response = await reArrangeGraph(this.data.map(node => node.id));
      if (response.success) {
        await this.fetchData();
      }
    } catch (error) {
      console.error('Error rearranging entities graph:', error);
      errorNotification('Failed to rearrange entities graph');
    }
    finally {
      this.setLoading(false);
    }
  }

  handleDownload = () => {
    if (this.onDownloadImage) {
      this.onDownloadImage();
    }
  }

  // ========== POPOVER STATE MANAGEMENT ==========
  setOpenPopoverId = (id) => {
    this.openPopoverId = id;
  }

  setEditingPopoverEdge = (edge) => {
    this.editingPopoverEdge = edge;
  }

  setTemporaryEdgeRemoveCallback = (callback) => {
    this.onTemporaryEdgeRemove = callback;
  }

  setDownloadImageCallback = (callback) => {
    this.onDownloadImage = callback;
  }

  closePopover = () => {
    if (this.openPopoverId && this.openPopoverId.startsWith('temp-')) {
      if (this.onTemporaryEdgeRemove) {
        this.onTemporaryEdgeRemove(this.openPopoverId);
      }
    }
    this.openPopoverId = null;
    this.editingPopoverEdge = null;
  }

  // Method to be called from components to remove temporary edges
  removeTemporaryEdge = (edgeId) => {
    // This will be called from the component that manages the edges state
    // The actual edge removal happens in the component, this is just for store consistency
    if (this.openPopoverId === edgeId) {
      this.closePopover();
      if (this.onTemporaryEdgeRemove) {
        this.onTemporaryEdgeRemove(edgeId);
      }
    }
  }

  // ========== SELECTION MANAGEMENT ==========
  setSelectedEntityId = (id) => {
    this.selectedEntityId = id;
  }

  setSelectedConnectionId = (id) => {
    this.selectedConnectionId = id;
  }

  setHoveredEntityId = (id) => {
    this.hoveredEntityId = id;
  }

  setCanvasLocked = (locked) => {
    this.isCanvasLocked = locked;
  }

  toggleCanvasLock = () => {
    this.isCanvasLocked = !this.isCanvasLocked;
  }

  // ========== DATA FETCHING ==========
  async fetchData() {
    this.setLoading(true);
    try {
      const [graphData, connectionsData] = await Promise.all([
        getHoldingEntityData(),
        getConnections()
      ]);

      // The new API returns a full graph structure with nodes and edges
      if (graphData.nodes && graphData.edges) {
        // Use the React Flow formatted data directly
        this.setData(graphData.nodes.map(node => ({
          id: parseInt(node.id),
          name: node.data.name,
          type: node.data.type,
          isDefault: node.data.isDefault,
          description: node.data.description,
          items: node.data.items || [],
          position: node.position,
          relationshipToPrimaryBeneficiary: node.data.relationshipToPrimaryBeneficiary,
          colorCode: this.getEntityColorForNode(node)
        })));

        this.setConnections(graphData.edges.map(edge => ({
          id: parseInt(edge.id),
          parentBeneficiaryId: parseInt(edge.source),
          childBeneficiaryId: parseInt(edge.target),
          ownershipPercentage: edge.data?.percentage || '',
          description: edge.data?.description || '',
          metadata: edge.data?.metadata || {}
        })));

      } else {
        // Fallback for old API format
        this.setData(graphData);
        this.setConnections(connectionsData || []);
      }

    } catch (error) {
      console.error('Error fetching beneficiary graph data:', error);
      errorNotification('Failed to load entity data');
    }
    finally {
      this.setLoading(false);
    }
  }

  // ========== DATA MANAGEMENT ==========
  normalizeEntity(entity) {
    if (!entity) return entity;
    const normalizeId = v => (typeof v === 'string' ? v : String(v));
    return {
      ...entity,
      id: normalizeId(entity.id),
      items: entity.items?.map(i => ({ ...i, id: normalizeId(i.id) })),
    };
  }

  getEntityColorForNode(node) {
    return node.style?.backgroundColor ||
      node.data?.colorCode ||
      getEntityColor(parseInt(node.id));
  }

  normalizeEntityArray(array) {
    return Array.isArray(array)
      ? array.map((entity, index) => {
        const norm = this.normalizeEntity(entity);
        return {
          ...norm,
          colorCode: norm.colorCode || getEntityColor(index),
        };
      })
      : array;
  }

  setData(data) {
    this.data = this.normalizeEntityArray(data);
  }

  setConnections(connections) {
    this.connections = connections;
    // Also update the legacy relationships array for backward compatibility
    this.relationships = connections.map(conn => ({
      id: conn.id,
      ownerId: conn.parentBeneficiaryId,
      ownedId: conn.childBeneficiaryId,
      percentage: conn.ownershipPercentage
    }));
  }

  // ========== ENTITY CRUD OPERATIONS ==========
  createHoldingEntity = async (data) => {
    try {

      const position = data.position || getNewPositionForNewNode(this.data);
      const result = await createHoldingEntity({
        name: data.name,
        description: data.description || '',
        colorCode: data.colorCode || '#FECACA',
        isDefault: data.isDefault || false,
        type: data.type,
        nodeData: { position }
      });

      await this.fetchData(); // always reload for consistency
      this.setShowAddModal(false);
      this.normalizeEntity(result.data);
      successNotification('Entity created successfully');

    } catch (error) {
      console.error('Error creating entity:', error);
      errorNotification('Failed to create entity');
      throw error;
    }
  }

  updateHoldingEntity = async (data, showMessage = true) => {
    try {
      const updatedData = {
        id: Number(data.id),
        name: data.name,
        description: data.description,
        colorCode: data.colorCode,
        isDefault: data.isDefault,
        type: data.type || null,
        nodeData: {
          position: data.position,
        }
      };

      if (data.relationshipToPrimaryBeneficiary) {
        updatedData.nodeData.relationshipToPrimaryBeneficiary = data.relationshipToPrimaryBeneficiary;
      }

      const result = await updateHoldingEntity(updatedData);

      await this.fetchData(); // always reload for consistency
      this.setShowEditModal(false);
      this.normalizeEntity(result.data);
      if (showMessage) {
        successNotification('Entity updated successfully');
      }

    } catch (error) {
      console.error('Error updating entity:', error);
      errorNotification('Failed to update entity');
      throw error;
    }
  }

  deleteHoldingEntity = async (id) => {
    try {
      const result = await deleteHoldingEntity(id);
      this.setShowDeleteModal(false);
      this.setSelectedEntityId(null);
      await this.fetchData();
      successNotification('Entity deleted successfully');
      return result;
    } catch (error) {
      console.error('Error deleting entity:', error);
      errorNotification('Failed to delete entity');
      throw error;
    }
  }

  // ========== CONNECTION/EDGE CRUD OPERATIONS ==========
  createConnection = async (sourceId, targetId, ownershipPercentage) => {
    this.setConnectionLoading('create', true);
    try {
      const result = await createConnection({
        parentBeneficiaryId: parseInt(sourceId),
        childBeneficiaryId: parseInt(targetId),
        ownershipPercentage: ownershipPercentage
      });

      successNotification('Connection created successfully');
      await this.fetchData();
      return result.data;
    } catch (error) {
      console.error('Error creating connection:', error);
      errorNotification('Failed to create connection');
      throw error;
    } finally {
      this.setConnectionLoading('create', false);
    }
  }

  updateConnection = async (connectionId, data) => {
    this.setConnectionLoading('update', true);
    try {

      const result = await updateConnection(connectionId, data);
      successNotification('Connection updated successfully');
      await this.fetchData();
      this.setShowEditEdgeModal(false);
      return result.data;
    } catch (error) {
      console.error('Error updating connection:', error);
      errorNotification('Failed to update connection');
      throw error;
    } finally {
      this.setConnectionLoading('update', false);
    }
  }

  deleteConnection = async (connectionId) => {
    this.setConnectionLoading('delete', true);
    try {
      const result = await deleteConnection(connectionId);
      successNotification('Connection deleted successfully');
      await this.fetchData();
      this.setShowEditEdgeModal(false);
      return result;
    } catch (error) {
      console.error('Error deleting connection:', error);
      errorNotification('Failed to delete connection');
      throw error;
    } finally {
      this.setConnectionLoading('delete', false);
    }
  }

  reassignConnection = async (connectionId, newTargetId) => {
    this.setConnectionLoading('update', true);
    try {
      const result = await reassignConnection(connectionId, newTargetId);
      successNotification('Connection reassigned successfully');
      await this.fetchData();
      return result.data;
    } catch (error) {
      console.error('Error reassigning connection:', error);
      errorNotification('Failed to reassign connection');
      throw error;
    } finally {
      this.setConnectionLoading('update', false);
    }
  }

  // ========== NODE POSITION UPDATE METHODS ==========

  // Public method - uses debounced version
  updateNodePosition = (nodeId, newPosition) => {
    this.debouncedUpdatePosition(nodeId, newPosition);
  }

  // Private immediate update method
  _updateNodePositionImmediate = async (nodeId, newPosition) => {
    if (this.isUpdatingNodePosition) return; // Prevent concurrent updates

    const entity = this.data.find(item => item.id + '' === nodeId);
    if (entity) {
      this.isUpdatingNodePosition = true;
      try {
        await this.updateHoldingEntity({
          ...entity,
          position: newPosition
        }, false);
      } catch (error) {
        console.error('Error updating node position:', error);
        // Don't show notification for position updates to avoid spam
      } finally {
        this.isUpdatingNodePosition = false;
      }
    }
  }



  nodesMapFunc = (entity, index) => ({
    id: entity.id + '',
    type: 'custom-entity-node',
    position: entity.position ?? ({ x: (index % 3) * (nodeWidth + nodeSpacing.x), y: Math.floor(index / 3) * (nodeHeight + nodeSpacing.y) }),
    data: {
      name: entity.name,
      type: entity.type,
      isDefault: entity.isDefault,
      color: entity.colorCode || getEntityColor(index),
      entity,
      label: entity.name,
      itemCount: (entity.items?.length || 0),
      description: entity.description
    },
    selected: entity.id + '' === this.selectedEntityId,
    draggable: true,
    selectable: true,
    connectable: true,
    deletable: false
  })

  async getEntitiesForReport(roots) {
    await this.fetchData();
    let nodes;
    let edges;
    if (roots) {
      // Collect all descendant ids starting from roots (BFS)
      const visited = new Set();
      const queue = [...roots.map(id => id + '')]; // ensure string ids

      while (queue.length > 0) {
        const currentId = queue.shift();
        if (!visited.has(currentId)) {
          visited.add(currentId);
          // Find all children of currentId
          this.connections.forEach(conn => {
            if ((conn.parentBeneficiaryId + '') === currentId) {
              const childId = conn.childBeneficiaryId + '';
              if (!visited.has(childId)) {
                queue.push(childId);
              }
            }
          });
        }
      }

      // Filter nodes in this.data whose id is in visited
      nodes = this.data.filter(node => visited.has(node.id + ''));
      edges = this.connections.filter(conn => nodes.some(node => node.id + '' === conn.parentBeneficiaryId + '') && nodes.some(node => node.id + '' === conn.childBeneficiaryId + ''));
    } else {
      nodes = this.data;
      edges = this.connections;
    }

    return this.mapData(nodes, edges);
  }

  mapData(nodes, edges) {
    const retNodes = nodes.map((entity, index) => ({
      id: entity.id + '',
      type: 'custom-entity-node',
      position: entity.position ?? ({ x: (index % 3) * (nodeWidth + nodeSpacing.x), y: Math.floor(index / 3) * (nodeHeight + nodeSpacing.y) }),
      data: {
        name: entity.name,
        type: entity.type,
        isDefault: entity.isDefault,
        color: entity.colorCode || getEntityColor(index),
        entity,
        label: entity.name,
        itemCount: (entity.items?.length || 0),
        description: entity.description
      },
      selected: entity.id + '' === this.selectedEntityId,
      draggable: true,
      selectable: true,
      connectable: true,
      deletable: false
    }));

    const retEdges = edges.map((conn) => ({
      id: conn.id + '',
      source: conn.parentBeneficiaryId + '',
      target: conn.childBeneficiaryId + '',
      type: 'custom-entity-edge',
      data: {
        ownership: conn.ownershipPercentage,
        label: `${conn.ownershipPercentage}%`,
        percentage: conn.ownershipPercentage,
        description: conn.description,
        metadata: conn.metadata
      },
      label: `${conn.ownershipPercentage}%`,
      animated: false,
      selectable: true,
      deletable: false,
      markerEnd: {
        type: MarkerType.ArrowClosed,
        width: 15,
        height: 15,
        color: '#ACB1BB'
      },
      selected: conn.id === this.selectedConnectionId
    }));

    // Do NOT auto-layout here; use backend positions
    return { nodes: retNodes, edges: retEdges };
  }

  // ========== COMPUTED PROPERTIES ==========
  get selectedEntity() {
    return this.data.find(item => item.id === this.selectedEntityId);
  }

  get selectedConnection() {
    return this.connections.find(conn => conn.id === this.selectedConnectionId);
  }

  get holdingEntities() {
    return this.metadataStore.holdingEntities.slice();
  }

  get graphData() {
    return this.mapData(this.data, this.connections);
  }

  // ========== CONVENIENCE GETTERS ==========
  get isAnyConnectionLoading() {
    return this.isCreatingConnection || this.isUpdatingConnection || this.isDeletingConnection;
  }

  get entities() {
    return this.data || [];
  }

  get sortedEntities() {
    return [...this.data].sort((a, b) => {
      const nameA = (a.name || '').toLowerCase();
      const nameB = (b.name || '').toLowerCase();
      return nameA.localeCompare(nameB);
    });
  }

  getEntityById = (entityId) => {
    if (!entityId) return null;
    return this.data.find(entity => entity.id + '' === entityId + '');
  }

  isEntityHovered = (entityId) => {
    return this.hoveredEntityId === entityId + '';
  }
}